import {
  type CreateMaintenanceTemplateRequest,
  type CreateMaintenanceTemplateScopeRequest,
  type MaintenanceTemplateListQuery,
  maintenanceTemplateDetailSchema,
  maintenanceTemplateListResponseSchema,
  maintenanceTemplateScopeSchema,
  maintenanceTemplateSourcePdfUploadResponseSchema,
  maintenanceTemplateVersionSchema,
  type UpdateMaintenanceTemplateRequest,
  type UpdateMaintenanceTemplateVersionRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { API_BASE_URL, apiFetch } from '@/lib/api';

export const maintenanceTemplatesKey = (query: MaintenanceTemplateListQuery) =>
  ['maintenance', 'templates', query] as const;

export const maintenanceTemplateKey = (id: string) => ['maintenance', 'template', id] as const;

export function maintenanceTemplateSourcePdfUrl(versionId: string): string {
  return `${API_BASE_URL}/maintenance/template-versions/${versionId}/source-pdf`;
}

export function useMaintenanceTemplates(query: MaintenanceTemplateListQuery) {
  const search = new URLSearchParams();
  search.set('page', String(query.page));
  search.set('pageSize', String(query.pageSize));
  search.set('sortBy', query.sortBy);
  search.set('sortOrder', query.sortOrder);
  if (query.q) {
    search.set('q', query.q);
  }
  if (query.isActive !== undefined) {
    search.set('isActive', String(query.isActive));
  }
  if (query.kind) {
    search.set('kind', query.kind);
  }
  return useQuery({
    queryKey: maintenanceTemplatesKey(query),
    queryFn: () =>
      apiFetch(
        `/maintenance/templates?${search.toString()}`,
        maintenanceTemplateListResponseSchema,
      ),
  });
}

export function useMaintenanceTemplate(id: string) {
  return useQuery({
    queryKey: maintenanceTemplateKey(id),
    queryFn: () => apiFetch(`/maintenance/templates/${id}`, maintenanceTemplateDetailSchema),
    enabled: Boolean(id),
  });
}

export function useCreateMaintenanceTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMaintenanceTemplateRequest) =>
      apiFetch('/maintenance/templates', maintenanceTemplateDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['maintenance', 'templates'] });
    },
  });
}

export function useDeleteMaintenanceTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/maintenance/templates/${id}`, z.object({ deleted: z.literal(true) }), {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['maintenance', 'templates'] });
    },
  });
}

export function usePatchMaintenanceTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateMaintenanceTemplateRequest }) =>
      apiFetch(`/maintenance/templates/${id}`, maintenanceTemplateDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async (_data, { id }) => {
      await client.invalidateQueries({ queryKey: ['maintenance', 'templates'] });
      await client.invalidateQueries({ queryKey: maintenanceTemplateKey(id) });
    },
  });
}

export function useUpdateMaintenanceTemplate(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMaintenanceTemplateRequest) =>
      apiFetch(`/maintenance/templates/${id}`, maintenanceTemplateDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['maintenance', 'templates'] });
      await client.invalidateQueries({ queryKey: maintenanceTemplateKey(id) });
    },
  });
}

export function useCreateMaintenanceTemplateVersion(templateId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/maintenance/templates/${templateId}/versions`, maintenanceTemplateDetailSchema, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: maintenanceTemplateKey(templateId) });
    },
  });
}

export function useReopenMaintenanceTemplateVersion(templateId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      apiFetch(
        `/maintenance/template-versions/${versionId}/reopen`,
        maintenanceTemplateDetailSchema,
        { method: 'POST' },
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: maintenanceTemplateKey(templateId) });
      await client.invalidateQueries({ queryKey: ['maintenance', 'published-versions'] });
    },
  });
}

export function useRetireMaintenanceTemplateVersion(templateId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      apiFetch(
        `/maintenance/template-versions/${versionId}/retire`,
        maintenanceTemplateDetailSchema,
        { method: 'POST' },
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: maintenanceTemplateKey(templateId) });
      await client.invalidateQueries({ queryKey: ['maintenance', 'templates'] });
      await client.invalidateQueries({ queryKey: ['maintenance', 'published-versions'] });
    },
  });
}

export function usePublishMaintenanceTemplateVersion(templateId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      apiFetch(
        `/maintenance/template-versions/${versionId}/publish`,
        maintenanceTemplateDetailSchema,
        {
          method: 'POST',
        },
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: maintenanceTemplateKey(templateId) });
      await client.invalidateQueries({ queryKey: ['maintenance', 'templates'] });
    },
  });
}

export function useUpdateMaintenanceTemplateVersion(templateId: string, versionId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMaintenanceTemplateVersionRequest) =>
      apiFetch(`/maintenance/template-versions/${versionId}`, maintenanceTemplateVersionSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: maintenanceTemplateKey(templateId) });
    },
  });
}

export function useAddMaintenanceTemplateScope(templateId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMaintenanceTemplateScopeRequest) =>
      apiFetch(`/maintenance/templates/${templateId}/scopes`, maintenanceTemplateScopeSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: maintenanceTemplateKey(templateId) });
      await client.invalidateQueries({ queryKey: ['maintenance', 'published-versions'] });
    },
  });
}

export function useUploadMaintenanceTemplatePdf(templateId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ versionId, file }: { versionId: string; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch(
        `${API_BASE_URL}/maintenance/template-versions/${versionId}/source-pdf`,
        { method: 'POST', body: form, credentials: 'include' },
      );
      if (!response.ok) {
        throw new Error('Upload fehlgeschlagen');
      }
      const json: unknown = await response.json();
      return maintenanceTemplateSourcePdfUploadResponseSchema.parse(json);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: maintenanceTemplateKey(templateId) });
    },
  });
}
